import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import { Breadcrumb } from 'antd';
import getRoutes from 'routes';
import { isI18nKey } from 'routes/utils/breadcrumbKeys';

import { RouteObject } from 'types/route';

import styles from './Breadcrumbs.module.scss';

interface BreadcrumbsProps {
  isHideOnHomePage?: boolean;
}

const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
  isHideOnHomePage = true
}) => {
  const location = useLocation();
  const { t } = useTranslation();
  const routes = getRoutes();
  // Search for the root route
  const rootRoute = routes.find((route: RouteObject) => route.path === '/');

  const pathSnippets = location.pathname.split('/').filter((i) => i);

  const findRouteByPath = (
    path: string,
    routes: RouteObject[]
  ): RouteObject | null => {
    for (const route of routes) {
      // Replacing dynamic parameters such as :id
      const regex = new RegExp(`^${route.path.replace(/:\w+/g, '[^/]+')}$`);
      if (regex.test(path)) {
        return route;
      }

      // If there are nested routes, recursively search in them
      if (route.children) {
        const childRoute = findRouteByPath(path, route.children);
        if (childRoute) {
          return childRoute;
        }
      }
    }

    return null;
  };

  // Generate other elements of breadcrumb
  const breadcrumbItems = pathSnippets.reduce<
    { title: React.ReactNode; key: string }[]
  >((acc, snippet, index) => {
    const path = `/${pathSnippets.slice(0, index + 1).join('/')}`;
    const route = findRouteByPath(path, routes);

    if (route && route.breadcrumb) {
      const translated = isI18nKey(route.breadcrumb)
        ? t(route.breadcrumb)
        : route.breadcrumb;
      acc.push({
        key: path,
        title:
          index === pathSnippets.length - 1 ? (
            <span>{translated}</span>
          ) : (
            <Link to={path}>{translated}</Link>
          )
      });
    }

    return acc;
  }, []);

  // Add the root element from the route configuration
  const breadcrumbItemsWithRoot = rootRoute
    ? [
        {
          key: rootRoute.path,
          title: (
            <Link to={rootRoute.path}>
              {isI18nKey(rootRoute.breadcrumb)
                ? t(rootRoute.breadcrumb)
                : rootRoute.breadcrumb}
            </Link>
          )
        },
        ...breadcrumbItems
      ]
    : breadcrumbItems;

  if (!breadcrumbItems.length && isHideOnHomePage) {
    return null;
  }

  return (
    <Breadcrumb
      className={styles.container}
      separator=">"
      items={breadcrumbItemsWithRoot}
    />
  );
};

export default Breadcrumbs;
